/**
 * CanvasPublishPanel
 * Shown inside a canvas product's edit form.
 * Performs the 3-write canvas publish flow:
 *   1. Shopify product create/update
 *   2. Shopify Files staging (3 images → GIDs)
 *   3. canvas_artwork metaobject upsert
 * Pre-populates image URLs from the product's saved presentation slots.
 */
import { useState, useEffect, useCallback } from "react";
import {
  Loader2, CheckCircle, XCircle, AlertCircle, ExternalLink,
  RefreshCw, ChevronDown, Upload, Wand2,
} from "lucide-react";
import adminApi from "@/lib/adminApi";
import { AdminSelect } from "./AdminSelect";

const CATEGORIES = [
  { slug: "nature-landscape",  label: "Nature & Landscape" },
  { slug: "city-architecture", label: "City & Architecture" },
  { slug: "animals-wildlife",  label: "Animals & Wildlife" },
  { slug: "abstract-modern",   label: "Abstract & Modern" },
  { slug: "travel-places",     label: "Travel & Places" },
  { slug: "black-white",       label: "Black & White" },
  { slug: "luxury-statement",  label: "Luxury & Statement" },
  { slug: "kids-playful",      label: "Kids & Playful" },
];

const STATUSES = ["active", "draft", "archived"];
const ORIENTATIONS = ["", "portrait", "landscape", "square"];

const STEP_COLORS = {
  ok: "text-green-400",
  failed: "text-red-400",
  skipped: "text-yellow-400",
};

function StepIndicator({ label, stepResult }) {
  const st = stepResult?.status;
  const colorClass = STEP_COLORS[st] || "text-white/40";
  return (
    <div className="flex items-start gap-2.5 py-1.5">
      <span className="mt-0.5">
        {!st || st === "pending"  ? <span className="inline-block w-3 h-3 rounded-full border border-white/20" /> : null}
        {st === "ok"              ? <CheckCircle size={13} className="text-green-400" /> : null}
        {st === "failed"          ? <XCircle     size={13} className="text-red-400"   /> : null}
        {st === "skipped"         ? <AlertCircle size={13} className="text-yellow-400" /> : null}
      </span>
      <div>
        <span className={`text-[11px] font-semibold ${colorClass}`}>
          {label}
        </span>
        {stepResult?.error && (
          <p className="text-[10px] text-red-400/70 mt-0.5 leading-snug">{stepResult.error}</p>
        )}
        {stepResult?.reason && (
          <p className="text-[10px] text-yellow-400/60 mt-0.5 leading-snug">{stepResult.reason}</p>
        )}
        {st === "ok" && stepResult?.id && (
          <p className="text-[10px] text-white/25 font-mono mt-0.5 truncate max-w-xs">{stepResult.id}</p>
        )}
      </div>
    </div>
  );
}

export default function CanvasPublishPanel({ productId, productData }) {
  const [open, setOpen]               = useState(false);
  const [form, setForm]               = useState({
    category:          "",
    artwork_image_url: "",
    front_mockup_url:  "",
    wall_mockup_url:   "",
    status:            "active",
    price_from:        "",
    short_description: "",
    featured:          false,
    orientation:       "",
    sort_order:        0,
    alt_text_main:     "",
    alt_text_front:    "",
    alt_text_wall:     "",
    seo_title:         "",
    seo_description:   "",
    collection_label:  "",
    tags:              "",
  });
  const [publishing, setPublishing]   = useState(false);
  const [result,     setResult]       = useState(null);
  const [categories, setCategories]   = useState(CATEGORIES);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Pre-fill from product data
  useEffect(() => {
    if (!productData) return;
    setForm((prev) => ({
      ...prev,
      category:          productData.canvas_category || productData.category || "",
      short_description: productData.description || "",
      seo_title:         productData.seo_title || productData.title || "",
      featured:          productData.featured || false,
      price_from:        productData.price ? String(productData.price) : "",
      tags:              Array.isArray(productData.tags) ? productData.tags.join(", ") : (productData.tags || ""),
    }));
  }, [productData]);

  // Load presentation gallery slots for auto-filling image URLs
  const loadSlotsImages = useCallback(async () => {
    if (!productId) return;
    setLoadingSlots(true);
    try {
      let slots = [];

      try {
        const presRes = await adminApi.get(`/api/admin/templates/presentations/${productId}`);
        const presSlots = presRes.data?.presentation?.slots || {};
        const presPack = presRes.data?.presentation?.template_pack_id;

        if (presPack && Object.keys(presSlots).length > 0) {
          slots = Object.entries(presSlots).map(([slotKey, slotValue]) => ({
            slot_key: slotKey,
            key: slotKey,
            storefront_role: slotValue?.storefront_role || "",
            asset_url: slotValue?.asset_url || "",
          }));
        }
      } catch (presErr) {
        console.error("[CanvasPublishPanel] load presentation slots:", presErr);
      }

      if (slots.length === 0) {
        const res = await adminApi.get(`/api/products/gallery/${productId}`);
        slots = res.data?.gallery || res.data?.slots || [];
      }

      const byKey = {};
      for (const slot of slots) {
        const url = (slot.asset_url || "").trim();
        if (!url) continue;
        if (slot.storefront_role) byKey[slot.storefront_role] = byKey[slot.storefront_role] || url;
        if (slot.slot_key) byKey[slot.slot_key] = byKey[slot.slot_key] || url;
        if (slot.key) byKey[slot.key] = byKey[slot.key] || url;
      }

      const galleryUrls = Array.isArray(productData?.gallery_images)
        ? productData.gallery_images.filter(Boolean)
        : [];
      const mainImage = (productData?.image_url || "").trim();

      const artworkImage =
        byKey.artwork_flat ||
        byKey.artwork ||
        galleryUrls[4] ||
        galleryUrls[galleryUrls.length - 1] ||
        byKey.hero_main ||
        byKey.hero ||
        byKey.main ||
        mainImage ||
        "";

      const frontMockupImage =
        byKey.hero_main ||
        byKey.hero ||
        mainImage ||
        byKey.wall_mockup_framed ||
        byKey.front_mockup ||
        galleryUrls[0] ||
        byKey.mockup ||
        artworkImage;

      const wallMockupImage =
        byKey.wall_mockup_framed ||
        galleryUrls[0] ||
        byKey.wall_mockup_unframed ||
        galleryUrls[1] ||
        byKey.wall_mockup ||
        byKey.room_lifestyle ||
        galleryUrls[3] ||
        byKey.room_scene ||
        byKey.room ||
        frontMockupImage;

      setForm((prev) => ({
        ...prev,
        artwork_image_url: artworkImage || prev.artwork_image_url,
        front_mockup_url: frontMockupImage || prev.front_mockup_url,
        wall_mockup_url: wallMockupImage || prev.wall_mockup_url,
        alt_text_main: prev.alt_text_main || productData?.title || "",
        alt_text_front: prev.alt_text_front || `${productData?.title || "Canvas artwork"} - front mockup`,
        alt_text_wall: prev.alt_text_wall || `${productData?.title || "Canvas artwork"} - wall mockup`,
      }));
    } catch (err) {
      console.error("[CanvasPublishPanel] loadSlotsImages:", err);
    } finally {
      setLoadingSlots(false);
    }
  }, [productData?.gallery_images, productData?.image_url, productData?.title, productId]);

  useEffect(() => {
    if (!open || !productId) return;
    if (form.artwork_image_url && form.front_mockup_url && form.wall_mockup_url) return;
    loadSlotsImages();
  }, [
    open,
    productId,
    form.artwork_image_url,
    form.front_mockup_url,
    form.wall_mockup_url,
    loadSlotsImages,
  ]);

  const handlePublish = async () => {
    if (!productId || !form.category || !form.artwork_image_url || !form.front_mockup_url || !form.wall_mockup_url) return;
    setPublishing(true);
    setResult(null);
    try {
      const res = await adminApi.post("/api/admin/shopify/canvas-publish", {
        local_product_id:  productId,
        category:          form.category,
        artwork_image_url: form.artwork_image_url,
        front_mockup_url:  form.front_mockup_url,
        wall_mockup_url:   form.wall_mockup_url,
        status:            form.status,
        price_from:        form.price_from ? parseFloat(form.price_from) : null,
        short_description: form.short_description,
        featured:          form.featured,
        orientation:       form.orientation,
        sort_order:        parseInt(form.sort_order, 10) || 0,
        alt_text_main:     form.alt_text_main,
        alt_text_front:    form.alt_text_front,
        alt_text_wall:     form.alt_text_wall,
        seo_title:         form.seo_title,
        seo_description:   form.seo_description,
        collection_label:  form.collection_label,
        tags:              form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      });
      setResult(res.data);
    } catch (err) {
      setResult({
        success: false,
        error:   err.response?.data?.detail || err.message,
        steps: {},
      });
    }
    setPublishing(false);
  };

  const f = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const inputCls  = "w-full bg-white/[0.04] border border-white/[0.08] text-white text-xs rounded px-2.5 py-2 outline-none focus:border-[#CBA052]/40";
  const labelCls  = "text-[9px] text-white/40 uppercase tracking-wider block mb-1";
  const alreadySynced = productData?.shopify_product_id || productData?.shopify_metaobject_id;

  return (
    <div className="border border-[#CBA052]/20 rounded" data-testid="canvas-publish-panel">
      {/* Header toggle */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-[#CBA052]/5 transition-colors"
        data-testid="canvas-publish-toggle"
      >
        <Wand2 size={14} className="text-[#CBA052] shrink-0" />
        <span className="text-xs font-bold text-[#CBA052]">Publish to Shopify</span>
        {alreadySynced && (
          <span className="text-[9px] text-green-400/70 bg-green-500/10 px-1.5 py-0.5 rounded border border-green-500/15 ml-1">
            synced
          </span>
        )}
        <ChevronDown size={12} className={`text-[#CBA052]/50 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-[#CBA052]/10 p-4 space-y-4">

          {/* Already synced info */}
          {alreadySynced && (
            <div className="p-2.5 rounded bg-green-500/8 border border-green-500/15 text-[10px] text-green-400/70">
              <p>This product is already synced to Shopify. Publishing again will update the existing product and metaobject.</p>
              {productData?.shopify_product_id && <p className="font-mono mt-0.5">Product ID: {productData.shopify_product_id}</p>}
              {productData?.shopify_metaobject_id && <p className="font-mono">Metaobject: {productData.shopify_metaobject_id}</p>}
            </div>
          )}

          {/* Required images section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] text-white/60 font-semibold uppercase tracking-wider">Required Images</p>
              <button
                onClick={loadSlotsImages}
                disabled={loadingSlots}
                className="flex items-center gap-1 text-[9px] text-[#CBA052]/60 hover:text-[#CBA052] border border-[#CBA052]/20 px-2 py-1 rounded transition-colors"
                data-testid="load-from-slots-btn"
              >
                {loadingSlots ? <Loader2 size={9} className="animate-spin" /> : <RefreshCw size={9} />}
                Load from Slots
              </button>
            </div>
            <p className="text-[10px] text-white/25 mb-3 leading-relaxed">
              These 3 images map to the <code className="font-mono text-[9px]">canvas_artwork</code> metaobject's{" "}
              <code className="font-mono text-[9px]">artwork_image</code>,{" "}
              <code className="font-mono text-[9px]">front_mockup_image</code>, and{" "}
              <code className="font-mono text-[9px]">wall_mockup_image</code> fields (all required, file_reference type).
              URLs must be publicly reachable — Shopify will pull them server-side.
            </p>
            <div className="space-y-2">
              {[
                { key: "artwork_image_url", label: "Artwork Image (flat artwork_image)", testid: "artwork-image-input" },
                { key: "front_mockup_url",  label: "Front Mockup Image (front_mockup_image)", testid: "front-mockup-input" },
                { key: "wall_mockup_url",   label: "Wall Mockup Image (wall_mockup_image)",   testid: "wall-mockup-input" },
              ].map(({ key, label, testid }) => (
                <label key={key} className="block">
                  <span className={labelCls}>{label}<span className="text-red-400 ml-0.5">*</span></span>
                  <input
                    value={form[key]}
                    onChange={f(key)}
                    placeholder="https://... or /api/admin/uploads/..."
                    className={inputCls}
                    data-testid={testid}
                  />
                </label>
              ))}
            </div>
          </div>

          {/* Core fields */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className={labelCls}>Category<span className="text-red-400 ml-0.5">*</span></span>
              <AdminSelect
                value={form.category}
                onChange={f("category")}
                size="default"
                data-testid="canvas-category-select"
              >
                <option value="">— Select category —</option>
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.label}</option>
                ))}
              </AdminSelect>
            </label>

            <label className="block">
              <span className={labelCls}>Status<span className="text-red-400 ml-0.5">*</span></span>
              <AdminSelect value={form.status} onChange={f("status")} size="default" data-testid="canvas-status-select">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </AdminSelect>
            </label>

            <label className="block">
              <span className={labelCls}>Price From (£)</span>
              <input type="number" step="0.01" value={form.price_from} onChange={f("price_from")} placeholder="e.g. 39.99" className={inputCls} data-testid="price-from-input" />
            </label>

            <label className="block">
              <span className={labelCls}>Orientation</span>
              <AdminSelect value={form.orientation} onChange={f("orientation")} size="default">
                {ORIENTATIONS.map((o) => <option key={o} value={o}>{o || "— not set —"}</option>)}
              </AdminSelect>
            </label>

            <label className="block">
              <span className={labelCls}>Sort Order</span>
              <input type="number" value={form.sort_order} onChange={f("sort_order")} placeholder="0" className={inputCls} />
            </label>

            <label className="block">
              <span className={labelCls}>Collection Label</span>
              <input value={form.collection_label} onChange={f("collection_label")} placeholder="e.g. Best Sellers" className={inputCls} />
            </label>
          </div>

          {/* Description + tags */}
          <label className="block">
            <span className={labelCls}>Short Description</span>
            <textarea value={form.short_description} onChange={f("short_description")} rows={2} className={`${inputCls} resize-none`} placeholder="Shown on product pages..." />
          </label>
          <label className="block">
            <span className={labelCls}>Tags (comma-separated)</span>
            <input value={form.tags} onChange={f("tags")} placeholder="e.g. canvas, abstract, modern" className={inputCls} />
          </label>

          {/* SEO */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className={labelCls}>SEO Title</span>
              <input value={form.seo_title} onChange={f("seo_title")} className={inputCls} />
            </label>
            <label className="block">
              <span className={labelCls}>SEO Description</span>
              <input value={form.seo_description} onChange={f("seo_description")} className={inputCls} />
            </label>
          </div>

          {/* Alt texts */}
          <div className="grid grid-cols-3 gap-3">
            {[["alt_text_main","Alt: Artwork"],["alt_text_front","Alt: Front Mockup"],["alt_text_wall","Alt: Wall Mockup"]].map(([key, lbl]) => (
              <label key={key} className="block">
                <span className={labelCls}>{lbl}</span>
                <input value={form[key]} onChange={f(key)} className={inputCls} />
              </label>
            ))}
          </div>

          {/* Featured toggle */}
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" checked={form.featured} onChange={f("featured")} className="accent-[#CBA052]" />
            <span className="text-xs text-white/60">Mark as Featured</span>
          </label>

          {/* Publish button */}
          <div className="flex items-center gap-4 pt-1">
            <button
              onClick={handlePublish}
              disabled={publishing || !form.category || !form.artwork_image_url || !form.front_mockup_url || !form.wall_mockup_url}
              className="flex items-center gap-2 text-xs font-bold text-black bg-[#CBA052] px-5 py-2.5 rounded hover:opacity-90 disabled:opacity-40 transition-opacity"
              data-testid="canvas-publish-btn"
            >
              {publishing ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              {publishing ? "Publishing..." : alreadySynced ? "Update on Shopify" : "Publish to Shopify"}
            </button>
            {result?.shopify_product_url && (
              <a
                href={result.shopify_product_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-[10px] text-[#CBA052]/70 hover:text-[#CBA052] transition-colors"
              >
                <ExternalLink size={10} /> View on Shopify
              </a>
            )}
          </div>

          {/* Step-by-step result */}
          {result && (
            <div
              className={`p-3 rounded border ${result.success ? "border-green-500/20 bg-green-500/5" : "border-red-500/20 bg-red-500/5"}`}
              data-testid="publish-result"
            >
              <p className={`text-[11px] font-bold mb-2 ${result.success ? "text-green-400" : "text-red-400"}`}>
                {result.success ? "Published successfully" : "Publish completed with errors"}
              </p>
              {result.error && <p className="text-[10px] text-red-400/70 mb-2">{result.error}</p>}
              {result.steps && Object.keys(result.steps).length > 0 && (
                <div className="space-y-0">
                  <StepIndicator label="1. Shopify Product"    stepResult={result.steps.shopify_product}    />
                  <StepIndicator label="2. Image Upload (GIDs)" stepResult={result.steps.image_upload}       />
                  <StepIndicator label="3. canvas_artwork Metaobject" stepResult={result.steps.shopify_metaobject} />
                  <StepIndicator label="4. Local DB update"    stepResult={result.steps.local_db}           />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
